/**
 * CoursePreviewModal — inline modal that renders a course EXACTLY as
 * a learner sees it on the public course display. Reuses the same
 * iframe strategy as LessonPreviewModal so we always match the real
 * public page without maintaining a duplicate renderer.
 *
 * Deep-links: CourseDetail.js reads `?admin_preview=1` and rewires
 * every lesson tile to postMessage the parent instead of navigating
 * the iframe. We listen for those messages here and pop a nested
 * LessonPreviewModal so admins can walk a whole syllabus without
 * ever losing the course preview context.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { ExternalLink, X } from 'lucide-react';
import LessonPreviewModal from './LessonPreviewModal';

export default function CoursePreviewModal({ open, onOpenChange, courseId, courseTitle }) {
  const [nestedLesson, setNestedLesson] = useState(null); // {id, title}

  // Listen for postMessage from the CourseDetail iframe when it runs
  // in admin_preview mode. The child page tells us which lesson the
  // admin clicked; we open a LessonPreviewModal on top so the course
  // preview stays open in the background.
  useEffect(() => {
    if (!open) return undefined;
    const onMsg = (e) => {
      const data = e?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'admin_preview_lesson' || !data.lessonId) return;
      setNestedLesson({ id: data.lessonId, title: data.lessonTitle || '' });
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [open]);

  // Clear the nested lesson when the outer modal closes so re-opening
  // the course preview starts on the syllabus page rather than in the
  // last-viewed lesson.
  useEffect(() => { if (!open) setNestedLesson(null); }, [open]);

  if (!courseId) return null;
  const src = `/course/${courseId}?admin_preview=1`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid="course-preview-modal"
      >
        <DialogHeader className="p-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-base truncate" data-testid="course-preview-title">
                Preview — {courseTitle || 'Course'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Rendered exactly as a learner will see the course. Click any lesson tile to preview it inline without leaving this modal.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => window.open(`/course/${courseId}`, '_blank', 'noopener,noreferrer')}
                data-testid="course-preview-open-tab-btn"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4 mr-1.5" />
                Open in tab
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="course-preview-close-btn"
                aria-label="Close preview"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-50 dark:bg-slate-900 overflow-hidden">
          <iframe
            title={`Preview: ${courseTitle || courseId}`}
            src={src}
            className="w-full h-full border-0"
            data-testid="course-preview-iframe"
          />
        </div>
      </DialogContent>
      {/* Nested lesson preview — opens on top of the course preview
          when the admin clicks any lesson tile inside the iframe. */}
      <LessonPreviewModal
        open={!!nestedLesson}
        onOpenChange={(v) => { if (!v) setNestedLesson(null); }}
        lessonId={nestedLesson?.id}
        lessonTitle={nestedLesson?.title}
      />
    </Dialog>
  );
}
